import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Close } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popper,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import UserStore from '../../store/UserStore';
import LoadingProgressStore from '../../store/LoadingProgressStore';
import LeadStore from '../../store/LeadStore';
import AuthStore from '../../store/AuthStore';
import { CustomStyledButton, CustomStyledDialog } from '../../utils/helper';

export const MassStatusUpdateDialog = observer(
  ({ toggleMassStatusUpdateDialog, handleToggleMassStatusUpdateDialog, selectedLeads }) => {
    const [workflowSubStatusList, setWorkflowSubStatusList] = useState([]);
    const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState(null);
    const [selectedWorkflowSubStatus, setSelectedWorkflowSubStatus] = useState(null);
    const [isSubStatusError, setIsSubStatusError] = useState(false);

    useEffect(() => {
      if (toggleMassStatusUpdateDialog) {
        UserStore.fetchUsersDropDown();
        LoadingProgressStore.toggleIsLoading(true);
        setSelectedWorkflowStatus(null);
        setSelectedWorkflowSubStatus(null);
        setWorkflowSubStatusList([]);
      }
    }, [toggleMassStatusUpdateDialog]);

    useEffect(() => {
      setIsSubStatusError(workflowSubStatusList?.length > 0 && !selectedWorkflowSubStatus);
    }, [selectedWorkflowSubStatus]);

    useEffect(() => {
      LeadStore.leadsMassStatusUpdateStatus && handleToggleMassStatusUpdateDialog();
    }, [LeadStore.leadsMassStatusUpdateStatus]);

    return (
      <CustomStyledDialog
        open={toggleMassStatusUpdateDialog}
        onClose={(event, reason) => reason !== 'backdropClick' && handleToggleMassStatusUpdateDialog(false)}
        fullWidth={true}
        maxWidth={'xs'}
        PaperProps={{
          component: 'form',
          onSubmit: (event) => {
            event.preventDefault();
            if (!selectedWorkflowStatus || (workflowSubStatusList?.length > 0 && !selectedWorkflowSubStatus)) {
              setIsSubStatusError(true);
              return;
            }
            LeadStore.massUpdateLeadStatus({
              leadId: selectedLeads,
              workFlowStatusId: selectedWorkflowStatus.id,
              workFlowSubStatusId: selectedWorkflowSubStatus ? selectedWorkflowSubStatus.id : null,
              userId: AuthStore.userDetails.Id,
            });
          },
        }}
      >
        <DialogTitle>
          <Stack>
            <Typography sx={{ fontWeight: '700' }} component="p">
              Mass Status Update
            </Typography>
          </Stack>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => handleToggleMassStatusUpdateDialog(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <DialogContent dividers>
          <Stack direction={'column'} spacing={2} sx={{ py: '10px' }}>
            {LeadStore.canPerformMassStatusUpdate ? (
              ''
            ) : (
              <Alert severity="warning">
                To perform a mass status update, please filter by selecting a single option in the "Workflow Status"
                column within the Leads Table.
              </Alert>
            )}
            <Autocomplete
              disablePortal
              id="select-workflow-status"
              options={LeadStore.workflowStatusByPipeLine}
              size="small"
              disabled={!LeadStore.canPerformMassStatusUpdate}
              renderOption={(props, option) => <li {...props}>{option.name}</li>}
              getOptionLabel={(option) => {
                return option.name;
              }}
              disableClearable
              onChange={(event, newValue) => {
                setSelectedWorkflowStatus(newValue);
                setWorkflowSubStatusList(newValue ? newValue.workflowSubStatusResponseDtos : []);
                !newValue && setSelectedWorkflowSubStatus(null);
              }}
              renderInput={(params) => <TextField {...params} label="Select Workflow Status" />}
            />
            <Autocomplete
              disablePortal
              id="select-workflow-sub-status"
              options={workflowSubStatusList}
              size="small"
              disabled={!LeadStore.canPerformMassStatusUpdate}
              value={selectedWorkflowSubStatus}
              renderOption={(props, option) => <li {...props}>{option.name}</li>}
              disableClearable
              getOptionLabel={(option) => {
                return option.name;
              }}
              onChange={(event, newValue) => {
                setSelectedWorkflowSubStatus(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} error={isSubStatusError} label="Select Workflow Sub Status" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: '20px', justifyContent: 'center' }}>
          <CustomStyledButton
            variant="contained"
            type="submit"
            autoFocus
            disabled={!LeadStore.canPerformMassStatusUpdate || !selectedWorkflowStatus}
          >
            Confirm
          </CustomStyledButton>
        </DialogActions>
      </CustomStyledDialog>
    );
  }
);
